import { MapPin, Home, User as UserIcon, Phone, MessageCircle, UserPlus2, Target, BadgeCheck } from "lucide-react";
import { StatusBadge, TempBadge, LeadTypeBadge } from "@/components/Badges";
import QuickStatusMenu from "@/components/QuickStatusMenu";

/** Lead card — matches mobile layout, works on desktop. */
export default function LeadCard({ lead, selected, onOpen, onSelectToggle, selectionMode, onCreateOpp }) {
  const openWhatsApp = (e) => {
    e.stopPropagation();
    const phone = String(lead.phone).replace(/[^0-9]/g, "");
    const url = `https://wa.me/${phone.startsWith("91") ? phone : "91" + phone}`;
    window.open(url, "_blank");
  };
  const call = (e) => {
    e.stopPropagation();
    // Dispatch tel: link so the browser handles it if a dialer is registered.
    const phone = String(lead.phone).replace(/[^0-9]/g, "");
    window.location.href = `tel:${phone}`;
    // Take the user to the lead detail page so they can log outcome there.
    onOpen?.(lead);
  };

  const handleClick = () => {
    if (selectionMode) onSelectToggle?.(lead.id);
    else onOpen?.(lead);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    onSelectToggle?.(lead.id);
  };

  return (
    <div
      data-testid={`lead-card-${lead.id}`}
      data-selected={selected}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      className="lead-card cursor-pointer soft-card p-4 anim-fade-up"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[15px] font-semibold text-navy">{lead.name}</div>
          <div className="mt-0.5 text-sm text-slate-500">{lead.phone}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusBadge status={lead.status} />
          <TempBadge temp={lead.temperature} />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-slate-600">
        {lead.location && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5 text-slate-400" /> {lead.location}
          </span>
        )}
        {lead.project && (
          <span className="inline-flex items-center gap-1">
            <Home className="h-3.5 w-3.5 text-slate-400" /> {lead.project}
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <LeadTypeBadge type={lead.lead_type} />
        <QuickStatusMenu lead={lead} />
        {lead.opportunity_id ? (
          <span
            data-testid={`lead-opp-created-${lead.id}`}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200"
          >
            <BadgeCheck className="h-3.5 w-3.5" /> Opportunity Created
          </span>
        ) : (
          <button
            data-testid={`lead-create-opp-${lead.id}`}
            onClick={(e) => { e.stopPropagation(); onCreateOpp?.(lead); }}
            className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--brand-navy))]/20 px-2.5 py-1 text-[11px] font-semibold text-navy transition-colors hover:bg-[hsl(var(--brand-navy))] hover:text-white"
          >
            <Target className="h-3.5 w-3.5" /> Create Opportunity
          </button>
        )}
      </div>

      {lead.created_by_name && (
        <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500">
          <UserPlus2 className="h-3 w-3 text-slate-400" />
          Added by <span className="font-medium text-slate-600">{lead.created_by_name}</span>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-dashed border-slate-100 pt-3">
        <span className="inline-flex items-center gap-1.5 text-[13px] text-slate-600">
          <UserIcon className="h-3.5 w-3.5 text-slate-400" />
          {lead.assigned_to_name || <span className="italic text-slate-400">Unassigned</span>}
        </span>
        <div className="flex gap-2">
          <button
            data-testid={`lead-wa-${lead.id}`}
            onClick={openWhatsApp}
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-600"
          >
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </button>
          <button
            data-testid={`lead-call-${lead.id}`}
            onClick={call}
            className="inline-flex items-center gap-1.5 rounded-full bg-gold px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:brightness-110"
          >
            <Phone className="h-3.5 w-3.5" /> Call
          </button>
        </div>
      </div>
    </div>
  );
}
